import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { scenariosAPI, catalogAPI } from '../../api';
import api from '../../api';
import VitalMonitor from '../../components/VitalMonitor';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';

// Configuración ECG + validación de coherencia + vista previa del monitor
function EcgConfigCard({ scenario, handleNestedChange }) {
  const [rhythms, setRhythms] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const est = scenario.estado_inicial || {};

  useEffect(() => {
    api.get('/ecg-rhythms').then(r => setRhythms(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!est.ritmo_ecg) { setWarnings([]); return; }
    const t = setTimeout(() => {
      api.post('/ecg-rhythms/validate', { ritmo_ecg: est.ritmo_ecg, fc: est.fc, qrs_type: est.qrs_type })
        .then(r => setWarnings(r.data.warnings || [])).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [est.ritmo_ecg, est.fc, est.qrs_type]);

  return (
    <Card className="mt-4" data-testid="ecg-config-card">
      <CardHeader>
        <CardTitle>Ritmo ECG del monitor</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Ritmo ECG</Label>
            <select value={est.ritmo_ecg || ''} className="w-full border rounded-lg px-3 py-2 text-sm"
              onChange={e => handleNestedChange('estado_inicial', 'ritmo_ecg', e.target.value || null)}
              data-testid="scenario-rhythm-select">
              <option value="">(automático según FC)</option>
              {rhythms.map(r => <option key={r.codigo} value={r.codigo}>{r.nombre}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Complejo QRS</Label>
            <select value={est.qrs_type || ''} className="w-full border rounded-lg px-3 py-2 text-sm"
              onChange={e => handleNestedChange('estado_inicial', 'qrs_type', e.target.value || null)}
              data-testid="scenario-qrs-select">
              <option value="">No especificado</option>
              <option value="angosto">Angosto (&lt;120 ms)</option>
              <option value="ancho">Ancho (≥120 ms)</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Duración QRS (ms)</Label>
            <Input type="number" min="40" max="300" value={est.qrs_duracion_ms || ''}
              onChange={e => handleNestedChange('estado_inicial', 'qrs_duracion_ms', e.target.value ? parseInt(e.target.value) : null)}
              data-testid="scenario-qrs-duration" />
          </div>
        </div>
        {warnings.length > 0 && (
          <div className="space-y-1" data-testid="ecg-coherence-warnings">
            {warnings.map((w, i) => (
              <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">⚠ {w}</p>
            ))}
          </div>
        )}
        <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} data-testid="preview-monitor-btn">
          {showPreview ? 'Ocultar vista previa' : 'Previsualizar monitor'}
        </Button>
        {showPreview && (
          <div className="h-72 relative" data-testid="monitor-preview">
            <VitalMonitor
              vitals={{
                fc: est.fc, spo2: est.spo2, pa_sistolica: est.pa_sistolica,
                pa_diastolica: est.pa_diastolica, fr: est.fr, temperatura: est.temperatura,
                ritmo_ecg: est.ritmo_ecg
              }}
              patientState="estable"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, 
  Trash, 
  FloppyDisk,
  ArrowLeft,
  Spinner,
  Info,
  Heartbeat,
  Target,
  Warning,
  ListBullets,
  Stethoscope,
  Flask,
  Image,
  ChatCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import SoftSkillsConfigTab from '../../components/scenarios/SoftSkillsConfigTab';
import XRConfigTab from '../../components/scenarios/XRConfigTab';

const SPECIALTIES = [
  { value: 'cardiologia', label: 'Cardiología' },
  { value: 'pediatria', label: 'Pediatría' },
  { value: 'emergencias', label: 'Emergencias' },
  { value: 'obstetricia', label: 'Obstetricia' },
  { value: 'neurologia', label: 'Neurología' },
  { value: 'atencion_primaria', label: 'Atención Primaria' },
  { value: 'comunicacion', label: 'Comunicación Clínica' },
  { value: 'trauma', label: 'Trauma' },
  { value: 'medicina_interna', label: 'Medicina Interna' },
];

const LEVELS = [
  { value: 'baja', label: 'Baja Fidelidad' },
  { value: 'media', label: 'Media Fidelidad' },
  { value: 'alta', label: 'Alta Fidelidad' },
];

const SIMULATION_TYPES = [
  { value: 'frecuente', label: 'Frecuente', description: 'Protocolos estrictos MINSAL. Casos clínicos frecuentes.' },
  { value: 'patologica', label: 'Patológica', description: 'Diagnóstico clínico avanzado. Razonamiento diferencial.' },
  { value: 'interactiva', label: 'Interactiva', description: 'Simulación libre. Múltiples desenlaces posibles.' },
];

const defaultScenario = {
  nombre: '',
  especialidad: 'emergencias',
  nivel: 'media',
  tipo_simulacion: 'frecuente',
  objetivos: [''],
  briefing: '',
  contexto_clinico: '',
  edad_paciente: 50,
  sexo_paciente: 'Masculino',
  antecedentes: '',
  tiempo_maximo: 600,
  estado_inicial: {
    fc: 80,
    spo2: 98,
    pa_sistolica: 120,
    pa_diastolica: 80,
    fr: 16,
    temperatura: 36.5,
    glasgow: 15,
    dolor: 0
  },
  paciente: {
    nombre: 'Paciente',
    edad: 50,
    sexo: 'Masculino',
    motivo_consulta: '',
    historia_actual: '',
    antecedentes_personales: [],
    alergias: [],
    medicacion_habitual: []
  },
  examen_fisico: {
    auscultacion_cardiaca: 'Ruidos cardíacos rítmicos, sin soplos',
    auscultacion_pulmonar: 'Murmullo vesicular conservado bilateral',
    palpacion_abdominal: 'Abdomen blando, depresible, indoloro',
    examen_neurologico: 'Glasgow 15, pupilas isocóricas reactivas',
    inspeccion_general: 'Paciente consciente, orientado, hidratado'
  },
  laboratorio: {
    hemoglobina: '14.5 g/dL',
    leucocitos: '7500/mm³',
    glucosa: '95 mg/dL',
    creatinina: '0.9 mg/dL',
    troponina: '< 0.04 ng/mL'
  },
  imagenes: {
    radiografia_torax: 'Silueta cardíaca normal, campos pulmonares limpios',
    ecg: 'Ritmo sinusal, FC 75 lpm, sin alteraciones del ST'
  },
  evaluacion: {
    criterios_tecnicos: [{ criterio: '', descripcion: '', puntos_maximos: 10, es_critico: false }],
    criterios_no_tecnicos: [{ criterio: '', descripcion: '', puntos_maximos: 10, es_critico: false }],
    tiempo_maximo: 600,
    errores_criticos: ['']
  },
  eventos: []
};

export default function ScenarioEditorPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;

  // v10.7.0 Fase 1 — tipo tomado desde ?type=... al crear (default clinical)
  const requestedType = searchParams.get('type') || 'clinical';
  const [scenario, setScenario] = useState({
    ...defaultScenario,
    scenario_type: requestedType,
  });
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [xrEnabled, setXrEnabled] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      loadScenario();
    }
    // Load XR feature flag once
    api.get('/xr/feature-flag').then((r) => setXrEnabled(!!r.data?.effective)).catch(() => {});
  }, [id]);

  const loadScenario = async () => {
    try {
      const response = await scenariosAPI.getById(id);
      setScenario({ ...defaultScenario, ...response.data });
    } catch (error) {
      toast.error('Error al cargar escenario');
      navigate('/app/scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setScenario(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setScenario(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setScenario(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const handleAddArrayItem = (field, defaultValue = '') => {
    setScenario(prev => ({
      ...prev,
      [field]: [...prev[field], defaultValue]
    }));
  };

  const handleRemoveArrayItem = (field, index) => {
    setScenario(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!scenario.nombre || !scenario.briefing) {
      toast.error('Complete los campos obligatorios: Nombre y Briefing');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...scenario,
        objetivos: scenario.objetivos.filter(o => o.trim()),
        evaluacion: {
          ...scenario.evaluacion,
          criterios_tecnicos: scenario.evaluacion.criterios_tecnicos.filter(c => c.criterio.trim()),
          criterios_no_tecnicos: scenario.evaluacion.criterios_no_tecnicos.filter(c => c.criterio.trim()),
          errores_criticos: scenario.evaluacion.errores_criticos.filter(e => e.trim())
        }
      };

      if (isEditMode) {
        await scenariosAPI.update(id, payload);
        toast.success('Escenario actualizado');
      } else {
        await scenariosAPI.create(payload);
        toast.success('Escenario creado');
      }
      navigate('/app/scenarios');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/app/scenarios')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="editor-title">
                  {isEditMode ? 'Editar Escenario' : 'Nuevo Escenario'}
                </h1>
                {(() => {
                  const st = scenario.scenario_type || 'clinical';
                  const meta = {
                    clinical:    { label: 'Clínico',            bg: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
                    soft_skills: { label: 'Habilidades Blandas', bg: 'bg-purple-50 text-purple-700 border-purple-200' },
                    hybrid:      { label: 'Híbrido',            bg: 'bg-amber-50 text-amber-700 border-amber-200' },
                  }[st] || { label: st, bg: 'bg-slate-100 text-slate-700 border-slate-200' };
                  return (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${meta.bg}`}
                      data-testid="editor-scenario-type-badge"
                    >
                      {meta.label}
                    </span>
                  );
                })()}
              </div>
              <p className="text-slate-500">
                {isEditMode ? 'Modifica los parámetros del escenario' : 'Crea un nuevo escenario clínico'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" data-testid="bulk-import-link-scenarios"
              onClick={() => navigate('/app/admin/bulk-import?module=scenarios')}>
              Carga Masiva
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-scenario-btn">
              {saving ? <Spinner size={18} className="animate-spin" /> : <FloppyDisk size={18} />}
              Guardar
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={(scenario.scenario_type === 'soft_skills' || scenario.scenario_type === 'hybrid') ? 'soft_skills' : 'general'} className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="general" className="gap-2">
              <Info size={16} />
              General
            </TabsTrigger>
            <TabsTrigger value="vitals" className="gap-2">
              <Heartbeat size={16} />
              Signos Vitales
            </TabsTrigger>
            <TabsTrigger value="patient" className="gap-2">
              <Stethoscope size={16} />
              Paciente
            </TabsTrigger>
            <TabsTrigger value="exams" className="gap-2">
              <Flask size={16} />
              Exámenes
            </TabsTrigger>
            <TabsTrigger value="evaluation" className="gap-2">
              <Target size={16} />
              Evaluación
            </TabsTrigger>
            {(scenario.scenario_type === 'soft_skills' || scenario.scenario_type === 'hybrid') && (
              <TabsTrigger value="soft_skills" className="gap-2" data-testid="tab-soft-skills">
                <ChatCircle size={16} />
                Habilidades Blandas
              </TabsTrigger>
            )}
            {xrEnabled && (
              <TabsTrigger value="xr" className="gap-2" data-testid="tab-xr">
                <ChatCircle size={16} />
                XR / Realidad Aumentada
              </TabsTrigger>
            )}
          </TabsList>

          {(scenario.scenario_type === 'soft_skills' || scenario.scenario_type === 'hybrid') && (
            <TabsContent value="soft_skills">
              <SoftSkillsConfigTab
                config={scenario.soft_skills_config || {}}
                onChange={(cfg) => setScenario((prev) => ({ ...prev, soft_skills_config: cfg }))}
              />
            </TabsContent>
          )}
          {xrEnabled && (
            <TabsContent value="xr">
              <XRConfigTab
                config={scenario.xr_config || {}}
                onChange={(cfg) => setScenario((prev) => ({ ...prev, xr_config: cfg }))}
              />
            </TabsContent>
          )}

          {/* General Tab */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>Información General</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nombre">Nombre del Escenario *</Label>
                    <Input
                      id="nombre"
                      value={scenario.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Ej: Infarto Agudo al Miocardio"
                      data-testid="scenario-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Especialidad</Label>
                      <Select value={scenario.especialidad} onValueChange={(v) => handleChange('especialidad', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SPECIALTIES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nivel de Fidelidad</Label>
                      <Select value={scenario.nivel} onValueChange={(v) => handleChange('nivel', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {LEVELS.map(l => (
                            <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Tipo de Simulación - para asignar a niveles */}
                <div className="space-y-2">
                  <Label>Tipo de Simulación (Nivel de Acceso)</Label>
                  <Select value={scenario.tipo_simulacion || 'frecuente'} onValueChange={(v) => handleChange('tipo_simulacion', v)}>
                    <SelectTrigger data-testid="simulation-type-select">
                      <SelectValue placeholder="Seleccionar tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SIMULATION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div className="flex flex-col">
                            <span className="font-medium">{t.label}</span>
                            <span className="text-xs text-slate-500">{t.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Este campo determina qué usuarios tienen acceso al escenario según su nivel de simulación asignado.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="briefing">Briefing / Contexto Clínico *</Label>
                  <Textarea
                    id="briefing"
                    value={scenario.briefing}
                    onChange={(e) => handleChange('briefing', e.target.value)}
                    placeholder="Describa el contexto clínico del paciente..."
                    rows={4}
                    data-testid="scenario-briefing"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edad">Edad del Paciente</Label>
                    <Input
                      id="edad"
                      type="number"
                      min="0"
                      max="120"
                      value={scenario.edad_paciente}
                      onChange={(e) => handleChange('edad_paciente', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sexo</Label>
                    <Select value={scenario.sexo_paciente} onValueChange={(v) => handleChange('sexo_paciente', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Masculino">Masculino</SelectItem>
                        <SelectItem value="Femenino">Femenino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tiempo">Tiempo Máximo (segundos)</Label>
                    <Input
                      id="tiempo"
                      type="number"
                      min="60"
                      max="3600"
                      value={scenario.tiempo_maximo}
                      onChange={(e) => handleChange('tiempo_maximo', parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="antecedentes">Antecedentes del Paciente</Label>
                  <Textarea
                    id="antecedentes"
                    value={scenario.antecedentes}
                    onChange={(e) => handleChange('antecedentes', e.target.value)}
                    placeholder="HTA, DM2, dislipidemia..."
                    rows={2}
                  />
                </div>

                {/* Objetivos */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Objetivos de Aprendizaje</Label>
                    <Button variant="outline" size="sm" onClick={() => handleAddArrayItem('objetivos', '')}>
                      <Plus size={16} className="mr-1" /> Agregar
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {scenario.objetivos.map((obj, i) => (
                      <div key={i} className="flex gap-2">
                        <Input
                          value={obj}
                          onChange={(e) => handleArrayChange('objetivos', i, e.target.value)}
                          placeholder={`Objetivo ${i + 1}`}
                        />
                        {scenario.objetivos.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveArrayItem('objetivos', i)}>
                            <Trash size={16} className="text-red-500" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vitals Tab */}
          <TabsContent value="vitals">
            <Card>
              <CardHeader>
                <CardTitle>Signos Vitales Iniciales</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>FC (lpm)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="300"
                      value={scenario.estado_inicial.fc}
                      onChange={(e) => handleNestedChange('estado_inicial', 'fc', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>SpO2 (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={scenario.estado_inicial.spo2}
                      onChange={(e) => handleNestedChange('estado_inicial', 'spo2', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PA Sistólica</Label>
                    <Input
                      type="number"
                      min="0"
                      max="300"
                      value={scenario.estado_inicial.pa_sistolica}
                      onChange={(e) => handleNestedChange('estado_inicial', 'pa_sistolica', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PA Diastólica</Label>
                    <Input
                      type="number"
                      min="0"
                      max="200"
                      value={scenario.estado_inicial.pa_diastolica}
                      onChange={(e) => handleNestedChange('estado_inicial', 'pa_diastolica', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>FR (rpm)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="60"
                      value={scenario.estado_inicial.fr}
                      onChange={(e) => handleNestedChange('estado_inicial', 'fr', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Temperatura (°C)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="30"
                      max="45"
                      value={scenario.estado_inicial.temperatura}
                      onChange={(e) => handleNestedChange('estado_inicial', 'temperatura', parseFloat(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Glasgow</Label>
                    <Input
                      type="number"
                      min="3"
                      max="15"
                      value={scenario.estado_inicial.glasgow}
                      onChange={(e) => handleNestedChange('estado_inicial', 'glasgow', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dolor (0-10)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      value={scenario.estado_inicial.dolor}
                      onChange={(e) => handleNestedChange('estado_inicial', 'dolor', parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <EcgConfigCard scenario={scenario} handleNestedChange={handleNestedChange} />
          </TabsContent>

          {/* Patient Tab */}
          <TabsContent value="patient">
            <Card>
              <CardHeader>
                <CardTitle>Perfil del Paciente (para Anamnesis IA)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Motivo de Consulta</Label>
                    <Textarea
                      value={scenario.paciente?.motivo_consulta || ''}
                      onChange={(e) => handleNestedChange('paciente', 'motivo_consulta', e.target.value)}
                      placeholder="Dolor torácico de 2 horas de evolución..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Historia Actual</Label>
                    <Textarea
                      value={scenario.paciente?.historia_actual || ''}
                      onChange={(e) => handleNestedChange('paciente', 'historia_actual', e.target.value)}
                      placeholder="Inicio súbito, opresivo, irradiado a brazo izquierdo..."
                      rows={3}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Exams Tab */}
          <TabsContent value="exams">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Stethoscope size={20} />
                    Hallazgos de Examen Físico
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(scenario.examen_fisico || {}).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <Label className="capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input
                        value={value}
                        onChange={(e) => handleNestedChange('examen_fisico', key, e.target.value)}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flask size={20} />
                    Resultados de Laboratorio
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(scenario.laboratorio || {}).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <Label className="capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Input
                        value={value}
                        onChange={(e) => handleNestedChange('laboratorio', key, e.target.value)}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image size={20} />
                    Hallazgos de Imágenes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Object.entries(scenario.imagenes || {}).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <Label className="capitalize">{key.replace(/_/g, ' ')}</Label>
                      <Textarea
                        value={value}
                        onChange={(e) => handleNestedChange('imagenes', key, e.target.value)}
                        rows={2}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Evaluation Tab */}
          <TabsContent value="evaluation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Warning size={20} className="text-red-500" />
                  Errores Críticos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {scenario.evaluacion.errores_criticos.map((error, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        value={error}
                        onChange={(e) => {
                          const newErrors = [...scenario.evaluacion.errores_criticos];
                          newErrors[i] = e.target.value;
                          handleNestedChange('evaluacion', 'errores_criticos', newErrors);
                        }}
                        placeholder={`Error crítico ${i + 1}`}
                      />
                      {scenario.evaluacion.errores_criticos.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => {
                          const newErrors = scenario.evaluacion.errores_criticos.filter((_, idx) => idx !== i);
                          handleNestedChange('evaluacion', 'errores_criticos', newErrors);
                        }}>
                          <Trash size={16} className="text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => {
                    handleNestedChange('evaluacion', 'errores_criticos', [...scenario.evaluacion.errores_criticos, '']);
                  }}>
                    <Plus size={16} className="mr-1" /> Agregar Error
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
